import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { UserAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import '../styles/sharedReports.css';

const SharedReports = () => {
  const { user } = UserAuth();
  const { lang } = useLanguage();
  const T = translations[lang].sharedReports;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const q = query(collection(db, 'sharedReports'), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setReports(data);
      } catch (err) {
        setError(T.error);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchReports();
  }, [user]);

  const handleDelete = async (report) => {
    if (!window.confirm(T.deleteConfirm(report.documentTitle || T.untitled))) return;
    setDeletingId(report.id);
    try {
      await deleteObject(ref(storage, report.storagePath));
      await deleteDoc(doc(db, 'sharedReports', report.id));
      setReports(prev => prev.filter(r => r.id !== report.id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(T.deleteAllConfirm(reports.length))) return;
    setDeletingAll(true);
    const failed = [];
    await Promise.all(reports.map(async (report) => {
      try {
        await deleteObject(ref(storage, report.storagePath));
        await deleteDoc(doc(db, 'sharedReports', report.id));
      } catch (err) {
        failed.push(report.id);
      }
    }));
    setReports(failed.length ? reports.filter(r => failed.includes(r.id)) : []);
    if (failed.length) alert(T.deleteAllFailed(failed.length));
    setDeletingAll(false);
  };

  if (loading) return <div className="sr-page"><p>{T.loading}</p></div>;
  if (error) return <div className="sr-page"><p className="sr-error">{error}</p></div>;

  return (
    <div className="sr-page">
      <div className="sr-header">
        <h1>{T.title}</h1>
        {reports.length > 0 && (
          <button
            className="sr-btn-delete"
            onClick={handleDeleteAll}
            disabled={deletingAll || deletingId !== null}
          >
            {deletingAll ? T.deleting : T.deleteAll}
          </button>
        )}
      </div>
      {reports.length === 0 ? (
        <p className="sr-empty">{T.empty}</p>
      ) : (
        <table className="sr-table">
          <thead>
            <tr>
              <th>{T.colDocument}</th>
              <th>{T.colCreated}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {reports.map(report => (
              <tr key={report.id}>
                <td>{report.documentTitle || T.untitled}</td>
                <td>
                  {report.createdAt
                    ? report.createdAt.toDate().toLocaleDateString(lang === 'fr' ? 'fr-CA' : undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                    : '—'}
                </td>
                <td>
                  <button
                    className="sr-btn-delete"
                    onClick={() => handleDelete(report)}
                    disabled={deletingId === report.id || deletingAll}
                  >
                    {deletingId === report.id ? T.deleting : T.delete}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SharedReports;
